// `useProposeLinks` — Phase 2d. Debounced auto-fire from the IssueEditor
// while the user is drafting. Returns ranked candidates the
// `ProposalsPanel` renders inline above the Save button.

import { useCallback, useRef, useState } from "react";

import {
  ProposeLinksResponseSchema,
  type IssueEditorPayload,
  type LinkCandidateRow,
} from "@loom/shared/dashboard";

import { httpPost } from "../lib/http";

export interface ProposeLinksState {
  readonly submit: (payload: IssueEditorPayload) => Promise<void>;
  readonly candidates: ReadonlyArray<LinkCandidateRow>;
  readonly loading: boolean;
  readonly methodologySkipped: boolean;
  readonly error: string | null;
}

export const useProposeLinks = (projectId: number): ProposeLinksState => {
  const [candidates, setCandidates] = useState<ReadonlyArray<LinkCandidateRow>>([]);
  const [loading, setLoading] = useState(false);
  const [methodologySkipped, setMethodologySkipped] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const seq = useRef(0);

  const submit = useCallback(
    async (payload: IssueEditorPayload): Promise<void> => {
      const myTick = ++seq.current;
      setLoading(true);
      setError(null);
      try {
        const res = await httpPost(
          `/api/projects/${projectId}/issues/draft/propose-links`,
          { payload },
          ProposeLinksResponseSchema,
        );
        // Drop the result if the user has typed more characters since.
        if (myTick !== seq.current) return;
        setCandidates(res.candidates);
        setMethodologySkipped(res.methodologySkipped);
      } catch (err) {
        if (myTick !== seq.current) return;
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (myTick === seq.current) setLoading(false);
      }
    },
    [projectId],
  );

  return { submit, candidates, loading, methodologySkipped, error };
};
